import { useInfiniteQuery } from '@tanstack/react-query'

import queries from 'src/queries'
import mentorCloudService, { GetFileRequest } from 'src/service/mentor-cloud-service'

import { mentorCloudExtensions, MentorCloudFileFilters } from 'src/types/files/file'

const DEFAULT_LIMIT = 30

export const useMentorCloudInfiniteFiles = (
  filters: MentorCloudFileFilters = { search: '', order: { column: 'name', ascending: false }, type: '*' },
  parentFolderId?: number | null
) => {
  const { order, search, type } = filters

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useInfiniteQuery({
    queryKey: queries.mentorCloud.files(parentFolderId || null, filters).queryKey,
    queryFn: async ({ pageParam = 0 }) => {
      let extensions: string[] = []

      switch (type) {
        case 'video':
          extensions = mentorCloudExtensions.video
          break
        case 'audio':
          extensions = mentorCloudExtensions.audio
          break
        case 'image':
          extensions = mentorCloudExtensions.image
          break
        case 'pdf':
          extensions = mentorCloudExtensions.pdf
          break
      }

      const payload: GetFileRequest = {}

      if (parentFolderId) {
        payload.filter_by = [
          {
            attribute: 'parent_folder_id',
            operator: '==',
            value: parentFolderId
          }
        ]
      } else {
        payload.filter_by = [
          {
            attribute: 'parent_folder_id',
            operator: 'is_null',
            value: true
          }
        ]
      }

      payload.filter_by.push(
        { attribute: 'name', operator: '!=', value: 'blob' },
        { attribute: 'name', operator: '!=', value: 'blob (1)' }
      )

      if (type !== '*' && extensions.length > 0) {
        payload.filter_by = [
          ...payload.filter_by,
          {
            attribute: 'extension',
            operator: 'in',
            value: extensions
          }
        ]
      }

      const {
        data: { result }
      } = await mentorCloudService.getFile({
        ...payload,
        limit: DEFAULT_LIMIT,
        offset: pageParam * DEFAULT_LIMIT,
        order_by: [order],
        search_by: [
          {
            attribute: 'tags',
            operator: 'ilike',
            value: search
          },
          {
            attribute: 'name',
            operator: 'ilike',
            value: search
          }
        ]
      })

      return result
    },
    getNextPageParam: (page, pages) => {
      if (page.length < DEFAULT_LIMIT) return null

      return pages.length
    },
    initialPageParam: 0
  })

  return {
    data,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetchingNextPage
  }
}
